import { SortDescriptor } from '@progress/kendo-data-query';
import { formatNumber } from '@progress/kendo-intl';
import { Button } from '@progress/kendo-react-buttons';
import { SelectionRange } from '@progress/kendo-react-dateinputs';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { GridColumn as Column, Grid, GridCustomCellProps, GridEvent, GridCustomHeaderCellProps, GridHandle, GridSortChangeEvent, GridToolbar } from '@progress/kendo-react-grid';
import { HeaderThElement } from '@progress/kendo-react-data-tools';
import { Checkbox, Input } from '@progress/kendo-react-inputs';
import { filterClearIcon, sortAscSmallIcon, sortDescSmallIcon } from '@progress/kendo-svg-icons';
import { ServiceTeam } from "TypeGen/service-team";
import dayjs from 'dayjs';
import * as React from 'react';
import { VehicleTypes } from 'views/AssetVehicles/Profile';
import { IDNameViewModel } from '../../../TypeGen/id-name-view-model';
import CustomerInput from '../../../components/CustomerInput';
import LoadingPanel from '../../../components/LoadingPanel';
import SimpleDateRangePicker from '../../../components/SimpleDateRangePicker';
import { fetchApi } from '../../../services/api';
import { openWindowPerc } from '../../../services/openWindow';
import { ILink } from '../../../types/link';
import { Title } from '../../../utils/title';
import { enumToArrayOfIDName } from '../../../utils/utils';
import CarrierAutoComplete from '../../AssetCarriers/CarrierAutoComplete';
import VehicleAutoComplete from '../../AssetVehicles/VehicleAutoComplete';
import { IconWrap } from '@progress/kendo-react-common';

type Props = {}

type State = {
  isLoading: boolean;
  data: InvoiceViewModel[];
  totalRecords: number;
  allowUnsort: boolean;
  multiple: boolean;
  advanceSearch: AdvanceSearchParameters;
  dateRange: SelectionRange;
  sortDesc: SortDescriptor[];
  billedVehicleTypeOptions: IDNameViewModel[];
}

type AdvanceSearchParameters = {
  orderNumberFilter: number;
  referenceNumberFilter: string;
  customerFilter: number;
  customerNumberFilter: string;
  carrierFilter: number;
  vehicleFilter: number;
  assetTypeFilter: string;
  shipFromDate?: Date;
  shipToDate?: Date;
  invoiceStatus?: number;
  serviceTeam?: number;
  billedVehicleTypeID: number;
  isDryRun: boolean;
  hasBalanceDue: boolean;
  isCrossDock: boolean;
  hasPayPosted: boolean;
  postBatchNumberFilter: number;
  sortDescriptors: SortDescriptor[];

  skip: number;
  take: number;
}

type InvoiceViewModel = {
  InvoiceID: number;
  InvoiceSequence: number;
  InvoiceLetter: string;
  OrderID: number;
  OrderNumber: number;
  InvoiceNumber: string;
  TotalCharges: number;
  ReferenceNumber1: string;
  InvoiceStatus: number;
  InvoiceStatusText: string;
  TotalMiles: number;
  PaperworkIn: boolean;
  OrderStatus: number;
  LoadType: LoadTypeModel;
  BillTo: CustomerViewModel;
  Shipper: StopCustomerViewModel;
  Consignee: StopCustomerViewModel;
  DaysSalesOutstanding: number;
  InvoiceQuickProBillLink: ILink;
}

type CustomerViewModel = {
  CustomerID: number;
  CustomerName: string;
  CustomerNumber: string;
  City: string;
  State: string;
  Zip: string;
  CustomerProfileLink: ILink;
}

type StopViewModel = {
  StopDateTime: Date;
}

type StopCustomerViewModel = CustomerViewModel & StopViewModel;

enum LoadTypeModel {
  NA,
  Asset,
  NonEPayCarrier,
  EPayCarrier
}

interface PageState {
  skip: number;
  take: number;
}

const PageSize = 50;

const InvoiceNumberHeaderCell = (props: GridCustomHeaderCellProps) => {
  const sort = props.columnMenuWrapperProps.sort?.find((d) => d.field === props.field);
  return (
    <HeaderThElement {...props.thProps}>
      <a className="k-link" onClick={props.onClick}>
        <span>
          Invoice Number
          <br />Status
          <br />Ref. 1
        </span>
        {sort && (
          <span className="k-sort-icon">
            <IconWrap icon={sort.dir === 'asc' ? sortAscSmallIcon : sortDescSmallIcon} />
          </span>
        )}
      </a>
    </HeaderThElement>
  );
};

class InvoiceNumberCell extends React.Component<GridCustomCellProps> {
  render() {
    const dataItem = this.props.dataItem as InvoiceViewModel;
    return <td {...this.props.tdProps}>
      <a
        href="#"
        style={{ color: '#007bff' }}
        onClick={(e) => {
          openWindowPerc(dataItem.InvoiceQuickProBillLink.Link, .9, .85);
          e.preventDefault();
        }}>{dataItem.InvoiceNumber}
      </a>
      <br />{dataItem.InvoiceStatusText}
      <br />{dataItem.ReferenceNumber1}
    </td>;
  }

  public shouldComponentUpdate(nextProps: GridCustomCellProps) {
    return this.props.dataItem[this.props.field] !== nextProps.dataItem[this.props.field];
  }
}

class StopCell extends React.Component<GridCustomCellProps> {
  render() {
    const dataItem = this.props.dataItem[this.props.field] as StopCustomerViewModel;
    return (<td {...this.props.tdProps}>
      <a
        href="#"
        style={{ color: '#007bff' }}
        onClick={(e) => {
          openWindowPerc(dataItem.CustomerProfileLink.Link);
          e.preventDefault();
        }}>{dataItem.CustomerNumber}
      </a>-{dataItem.CustomerName}
      <br />{dataItem.City}, {dataItem.State}
      <br />{dayjs.utc(dataItem.StopDateTime).tz("America/New_York").format("MM/DD/YYYY HH:mm")}
    </td>);
  }

  public shouldComponentUpdate(nextProps: GridCustomCellProps) {
    return this.props.dataItem[this.props.field] !== nextProps.dataItem[this.props.field];
  }
}

const TotalChargesHeaderCell = (props: GridCustomHeaderCellProps) => {
  return (
    <HeaderThElement {...props.thProps}>
      <div style={{ textAlign: "right" }}>Total Charges</div>
      <div style={{ textAlign: "right" }}>Miles</div>
    </HeaderThElement>
  );
};

class TotalChargesCell extends React.Component<GridCustomCellProps> {
  render() {
    const dataItem = this.props.dataItem as InvoiceViewModel;
    return (<td {...this.props.tdProps} style={{ textAlign: "right" }}>
      {formatNumber(this.props.dataItem.TotalCharges, "c")}
      <br />{dataItem.TotalMiles} mi.
      {dataItem.DaysSalesOutstanding > -1 &&
        <>
          <br />
          DSO: {dataItem.DaysSalesOutstanding}
        </>
      }
    </td>);
  }

  public shouldComponentUpdate(nextProps: GridCustomCellProps) {
    return this.props.dataItem[this.props.field] !== nextProps.dataItem[this.props.field];
  }
}

class CustomerCell extends React.Component<GridCustomCellProps> {
  render() {
    const dataItem = this.props.dataItem[this.props.field] as CustomerViewModel;
    return (<td {...this.props.tdProps}>
      <a
        href="#"
        style={{ color: '#007bff' }}
        onClick={(e) => {
          openWindowPerc(dataItem.CustomerProfileLink.Link);
          e.preventDefault();
        }}>{dataItem.CustomerNumber}
      </a>-{dataItem.CustomerName}
      <br />{dataItem.City}, {dataItem.State}
    </td>);
  }

  public shouldComponentUpdate(nextProps: GridCustomCellProps) {
    return this.props.dataItem[this.props.field] !== nextProps.dataItem[this.props.field];
  }
}

export default class InvoiceListing extends React.Component<Props, State> {
  private gridRef = React.createRef<GridHandle>();
  private appendRequestInProgress = false;
  private minDate = dayjs().subtract(3, "years").toDate();
  private maxDate = dayjs().add(7, "days").toDate();

  private invoiceStatuses = [
    { value: 0, text: 'Any Status' },
    //{ value: 90, text: 'Rated/Discrepancy' },
    { value: 95, text: 'Unaudited' },
    { value: 100, text: 'Rated' },
    { value: 101, text: 'Rated In Billing, POD In' },
    { value: 102, text: 'Rated In Billing, BOL In' },
    { value: 103, text: 'Rated In Billing, POD/BOL In' },
    { value: 105, text: 'Discrepancy' },
    { value: 110, text: 'Ready to Post' },
    /*{ value: 115, text: 'Printed' }, Not used in TMS */
    { value: 120, text: 'Posted' },
  ]

  private serviceTeams = [{ ID: 0, Name: 'Any Service Team' }].concat(enumToArrayOfIDName(ServiceTeam));
  private SESSION_STORAGE_NAME: string = "InvoiceListingFilterV1";
  constructor(props: Props) {
    super(props);

    const sessionStorageAndFilter = sessionStorage.getItem(this.SESSION_STORAGE_NAME);

    let filterSortState: any;
    if (sessionStorageAndFilter) {
      filterSortState = JSON.parse(sessionStorageAndFilter);
      filterSortState.dateRange.start = dayjs(filterSortState.dateRange.start).utc().toDate();
      filterSortState.dateRange.end = dayjs(filterSortState.dateRange.end).utc().toDate();
      filterSortState.advanceSearch.shipFromDate = dayjs(filterSortState.advanceSearch.shipFromDate).utc().toDate();
      filterSortState.advanceSearch.shipToDate = dayjs(filterSortState.advanceSearch.shipToDate).utc().toDate();
      filterSortState.advanceSearch.billedVehicleTypeID = filterSortState.advanceSearch.billedVehicleTypeID || 0;
    } else {
      filterSortState = { ...this.resetFilterSortState() };
    }

    this.state = {
      isLoading: false,
      data: [],
      totalRecords: 0,
      allowUnsort: true,
      multiple: true,
      billedVehicleTypeOptions: [{ ID: 0, Name: 'Any Vehicle Type' }, ...VehicleTypes],
      ...filterSortState
    };

    this.filterClear = this.filterClear.bind(this);
    this.refreshData = this.refreshData.bind(this);
    this.scrollHandler = this.scrollHandler.bind(this);
    this.sortChange = this.sortChange.bind(this);
  }

  private defaultSort = [{ field: "InvoiceNumber", dir: "asc" }] as SortDescriptor[];
  private defaultPageState = { skip: 0, take: PageSize } as PageState;

  private resetFilterSortState() {
    return {
      advanceSearch: {
        orderNumberFilter: 0, referenceNumberFilter: "", customerFilter: 0, customerNumberFilter: '', invoiceStatus: 100, serviceTeam: 0, carrierFilter: 0, vehicleFilter: 0, billedVehicleTypeID: 0,
        shipFromDate: dayjs().subtract(1, "month").startOf('day').utc().toDate(),
        shipToDate: dayjs().endOf('day').utc().toDate(),
        assetTypeFilter: 'All Loads', postBatchNumberFilter: 0,
        isDryRun: false, isCrossDock: false, hasBalanceDue: false, hasPayPosted: false,
        page: 1, pageSize: PageSize,
        sortDescriptors: [...this.defaultSort],
        skip: this.defaultPageState.skip,
        take: this.defaultPageState.take
      } as AdvanceSearchParameters,
      dateRange: {
        start: dayjs().subtract(1, "month").startOf('day').utc().toDate(),
        end: dayjs().endOf('day').utc().toDate()
      } as SelectionRange,
      sortDesc: [...this.defaultSort]
    };
  }

  sortChange = (event: GridSortChangeEvent) => { //only support single sort currently      
    if (event.sort.length === 0)
      event.sort = [...this.defaultSort];

    //this.state.advanceSearch.skip = this.defaultPageState.skip;
    //this.state.advanceSearch.take = this.defaultPageState.take;

    this.setState({ advanceSearch: { ...this.state.advanceSearch, sortDescriptors: event.sort } }, () => { this.refreshData() });
  }

  scrollHandler = (event: GridEvent) => {
    const scrollElement = event.syntheticEvent.currentTarget as HTMLElement;
    const isNearBottom = scrollElement.scrollHeight > scrollElement.clientHeight
      && scrollElement.scrollTop + scrollElement.clientHeight >= scrollElement.scrollHeight - 10;

    if (!isNearBottom || this.appendRequestInProgress || this.state.data.length >= this.state.totalRecords)
      return;

    this.fetch(true, this.state.data.length);
  };

  private filterClear() {
    this.setState({ ...this.resetFilterSortState() }, () => { this.refreshData() });
  }

  private refreshData() {
    this.gridRef.current?.element?.querySelector<HTMLElement>('.k-grid-content')?.scrollTo(0, 0);
    this.setState(prevState => ({
      advanceSearch: {
        ...prevState.advanceSearch,
        skip: this.defaultPageState.skip,
        take: this.defaultPageState.take
      }
    }), () => this.fetch(false));
  }

  onChange = (e: SelectionRange) => {
    if (dayjs(e.start).year() >= dayjs(this.minDate).year() && dayjs(e.end).diff(dayjs(e.start), 'years') >= 1) {
      e.end = dayjs(e.start).add(1, "years").toDate();
    }

    this.setState({
      dateRange: e, advanceSearch: {
        ...this.state.advanceSearch,
        shipFromDate: dayjs(e.start).startOf('day').utc().toDate(),
        shipToDate: dayjs(e.end).endOf('day').utc().toDate()
      }
    });
  }

  public render() {
    return (<>
      <Title string="Invoice Listing" />
      <h4 className="text-left mt-3">Invoice Listing</h4>
      {this.state.isLoading && <LoadingPanel />}
      <Grid
        ref={this.gridRef}
        style={{ maxHeight: '80vh' }}
        data={this.state.data}
        scrollable="scrollable"
        resizable={true}
        sort={this.state.advanceSearch.sortDescriptors}
        onSortChange={this.sortChange}
        sortable={true}
        onScroll={this.scrollHandler}
      >
        <GridToolbar overflow="none">
          <Input placeholder="Order No." type="number"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if (this.state.advanceSearch.orderNumberFilter > 0) {
                  e.preventDefault();
                  this.refreshData();
                }
              }
            }}
            value={this.state.advanceSearch.orderNumberFilter > 0 ? this.state.advanceSearch.orderNumberFilter : ""}
            onChange={(value) => {
              this.setState({ advanceSearch: { ...this.state.advanceSearch, orderNumberFilter: Number(value.value) } });
            }} />

          <Input placeholder="Ref No."
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if (this.state.advanceSearch.referenceNumberFilter.length >= 3) {
                  e.preventDefault();
                  this.refreshData();
                }
              }
            }}
            value={this.state.advanceSearch.referenceNumberFilter} onChange={(value) => {
              this.setState({ advanceSearch: { ...this.state.advanceSearch, referenceNumberFilter: value.value } });
            }} />

          <DropDownList
            //style={{ width: "300px" }}
            data={['All Loads', 'Asset Loads', 'Carrier Loads']}
            value={['All Loads', 'Asset Loads', 'Carrier Loads'].find(x => x === this.state.advanceSearch.assetTypeFilter)}
            onChange={(e) => this.setState({ advanceSearch: { ...this.state.advanceSearch, assetTypeFilter: e.value } })}
          // defaultValue="t"
          />
          <DropDownList
            style={{ width: 150 }}
            defaultValue={this.serviceTeams[0]}
            value={this.serviceTeams.find(x => x.ID === this.state.advanceSearch.serviceTeam)}
            data={this.serviceTeams}
            textField="Name"
            dataItemKey="ID"
            onChange={(e) => this.setState({ advanceSearch: { ...this.state.advanceSearch, serviceTeam: e.value.ID } })}
          />
          <DropDownList
            style={{ width: 200 }}
            defaultValue={this.state.billedVehicleTypeOptions[0]}
            value={this.state.billedVehicleTypeOptions.find(x => x.ID === this.state.advanceSearch.billedVehicleTypeID)}
            data={this.state.billedVehicleTypeOptions}
            textField="Name"
            dataItemKey="ID"
            onChange={(e) => this.setState({ advanceSearch: { ...this.state.advanceSearch, billedVehicleTypeID: e.value.ID } })}
          />
          <DropDownList
            style={{ width: 250 }}
            defaultValue={0}
            value={this.invoiceStatuses.find(x => x.value === this.state.advanceSearch.invoiceStatus)}
            data={this.invoiceStatuses}
            textField="text"
            onChange={(e) => this.setInvoiceStatus(e)}
          />
          <Input placeholder="Post Batch No." type="number" width={100}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if (this.state.advanceSearch.postBatchNumberFilter > 0) {
                  e.preventDefault();
                  this.refreshData();
                }
              }
            }}
            value={this.state.advanceSearch.postBatchNumberFilter > 0 ? this.state.advanceSearch.postBatchNumberFilter : ""}
            onChange={(value) => {
              this.setState({ advanceSearch: { ...this.state.advanceSearch, postBatchNumberFilter: Number(value.value) } });
            }} />
          <Checkbox
            className='align-self-center'
            value={this.state.advanceSearch.isDryRun}
            onChange={(e) => this.setState({
              advanceSearch: {
                ...this.state.advanceSearch,
                isDryRun: e.value,
                isCrossDock: false
              }
            })}
            label={"Is Dry Run"}
          />
          <Checkbox
            className='align-self-center'
            value={this.state.advanceSearch.isCrossDock}
            onChange={(e) => this.setState({
              advanceSearch: {
                ...this.state.advanceSearch,
                isCrossDock: e.value,
                isDryRun: false
              }
            })}
            label={"Is Cross Dock"}
          />
          <Checkbox
            className='align-self-center'
            value={this.state.advanceSearch.hasBalanceDue}
            onChange={(e) => this.setState({ advanceSearch: { ...this.state.advanceSearch, hasBalanceDue: e.value } })}
            label={"Has Balance Due"}
          />
          <Checkbox
            className='align-self-center'
            value={this.state.advanceSearch.hasPayPosted}
            onChange={(e) => this.setState({ advanceSearch: { ...this.state.advanceSearch, hasPayPosted: e.value } })}
            label={"Has Pay Posted"}
          />
        </GridToolbar>
        <GridToolbar className="kendo-grid-toolbar-overflow-visible" overflow="none">
          <div className="input-group" style={{ width: '202px' }}>
            <CustomerInput
              placeholder='Company'
              CustomerID={this.state.advanceSearch?.customerFilter}
              CustomerNumber={this.state.advanceSearch?.customerNumberFilter}
              onChange={(e) => {
                this.setState({ advanceSearch: { ...this.state.advanceSearch, customerFilter: e?.CustomerID, customerNumberFilter: e?.CustomerNumber } });
              }}
            />
          </div>

          <CarrierAutoComplete
            placeholder="Carrier"
            selectedCarrierID={this.state.advanceSearch?.carrierFilter}
            style={{ width: '175px' }}
            onSelectedCarrier={(e) => {
              this.setState({ advanceSearch: { ...this.state.advanceSearch, carrierFilter: e?.CarrierID } });
            }}
          />

          <VehicleAutoComplete
            placeholder='Vehicle'
            style={{ width: '175px' }}
            //style={{ flex: 1, borderTopRightRadius: 0, borderBottomRightRadius: 0 }}

            selectedVehicleID={this.state.advanceSearch?.vehicleFilter}
            //setFocus={true}
            onSelectedVehicle={(e) => this.setState({ advanceSearch: { ...this.state.advanceSearch, vehicleFilter: e?.VehicleID } })}
          />

          <span>
            <label className="mt-1">Pick-Up Date Range:</label>
            <SimpleDateRangePicker
              min={this.minDate}
              max={this.maxDate}
              value={this.state.dateRange}
              onChange={(e) => {
                this.onChange(e);
              }}
            />
          </span>
          <Button
            title="Reset Filters"
            icon="filter-clear"
            className="ml-2 mr-2"
            svgIcon={filterClearIcon}
            onClick={this.filterClear}
          />
          <Button
            style={{ width: 80 }}
            themeColor="primary"
            title="Search"
            onClick={this.refreshData}
          >Search</Button>
        </GridToolbar>
        <Column field="InvoiceNumber" cells={{ headerCell: InvoiceNumberHeaderCell, data: InvoiceNumberCell }} />
        <Column field="Shipper" title="Shipper" cells={{ data: StopCell }} />
        <Column field="Consignee" title="Consignee" cells={{ data: StopCell }} />
        <Column field="BillTo" title="Customer" cells={{ data: CustomerCell }} />
        <Column field="TotalCharges" title="Total Charges" cells={{ headerCell: TotalChargesHeaderCell, data: TotalChargesCell }} sortable={false} />
        {/*<Column field="InvoiceNumber" title="Info" cells={{ headerCell: PaperworkInHeaderCell }} cells={{ data: PaperworkInCell }} sortable={false} />*/}
      </Grid>
      {
        this.state.totalRecords > 0 &&
        <div className="k-pager k-pager-md k-grid-pager">
          <div style={{ marginLeft: "auto", marginRight: 0 }}>
            {formatNumber(Math.min(this.state.data.length, this.state.totalRecords), 'n0')} of {formatNumber(this.state.totalRecords, 'n0')} items
          </div>
        </div>
      }
    </>);
  }

  setInvoiceStatus(e: DropDownListChangeEvent): void {
    this.setState({ advanceSearch: { ...this.state.advanceSearch, invoiceStatus: e.value.value } });
  }

  setFilterSessionStorage(): void {
    sessionStorage.setItem(this.SESSION_STORAGE_NAME, JSON.stringify({ advanceSearch: { ...this.state.advanceSearch }, dateRange: { ...this.state.dateRange }, sortDesc: [...this.state.sortDesc] }));
  }

  public componentDidMount() {
    this.fetch(false);

    window.addEventListener('message', this.handleMessage);
  }

  public componentWillUnmount() {
    window.removeEventListener('message', this.handleMessage);
  }

  handleMessage = (e: MessageEvent) => {
    if (e.type === "message" && e.data?.type === "refresh") {
      this.refreshData();
    }
  }

  private fetch(append: boolean, skip = this.state.advanceSearch.skip) {
    if (dayjs(this.state.advanceSearch.shipFromDate).isValid() === false || dayjs(this.state.advanceSearch.shipToDate).isValid() === false)
      return;

    if (append && this.appendRequestInProgress)
      return;

    if (append)
      this.appendRequestInProgress = true;

    this.setState({ isLoading: true })

    const data = { ...this.state.advanceSearch, skip } as AdvanceSearchParameters;

    fetchApi(`/api/billing/invoicelisting`, data, 'POST')
      .then(({ Data, Total }) => {
        if (!append)
          this.setState({
            isLoading: false, data: Data, totalRecords: Total,
            advanceSearch: { ...this.state.advanceSearch }
          }, () => this.setFilterSessionStorage());
        else {
          this.setState(prevState => ({
            isLoading: false,
            data: prevState.data.concat(Data),
            totalRecords: Total,
            advanceSearch: { ...prevState.advanceSearch, skip }
          }));
        }
      })
      .finally(() => {
        if (append)
          this.appendRequestInProgress = false;
      });
  }
}
